import { paiseToRupees, rupeesToPaise } from "@templeos/shared";
import { useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";

interface FieldProps {
  label: ReactNode;
  error?: string | null;
  hint?: ReactNode;
  required?: boolean;
  children: (id: string, describedBy: string | undefined, invalid: boolean) => ReactNode;
}

/** Label + control + hint + error, wired with ids for screen readers. */
export function Field({ label, error, hint, required, children }: FieldProps) {
  const id = useId();
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(" ") || undefined;
  return (
    <div className="tos-field">
      <label className="tos-label" htmlFor={id}>{label}{required && <span className="tos-req" aria-hidden="true"> *</span>}</label>
      {children(id, describedBy, Boolean(error))}
      {hint && !error && <span id={hintId} className="tos-hint">{hint}</span>}
      {error && <span id={errId} className="tos-error">{error}</span>}
    </div>
  );
}

type Base = { label: ReactNode; error?: string | null; hint?: ReactNode };

export function TextField({ label, error, hint, required, ...rest }: Base & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {(id, d, invalid) => <input id={id} className="tos-input" aria-describedby={d} aria-invalid={invalid || undefined} required={required} {...rest} />}
    </Field>
  );
}

export function SelectField({ label, error, hint, required, options, placeholder, ...rest }: Base & SelectHTMLAttributes<HTMLSelectElement> & {
  options: { value: string; label: string }[]; placeholder?: string;
}) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {(id, d, invalid) => (
        <select id={id} className="tos-select" aria-describedby={d} aria-invalid={invalid || undefined} required={required} {...rest}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </Field>
  );
}

export function TextAreaField({ label, error, hint, required, ...rest }: Base & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <Field label={label} error={error} hint={hint} required={required}>
      {(id, d, invalid) => <textarea id={id} className="tos-textarea" aria-describedby={d} aria-invalid={invalid || undefined} required={required} {...rest} />}
    </Field>
  );
}

export function Checkbox({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <label className="tos-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/** Rupee input that reports integer paise (null while empty or invalid). */
export function MoneyField({ label, value, onChange, error, hint, required, disabled, autoFocus }: Base & {
  value: number | null; onChange: (paise: number | null) => void; required?: boolean; disabled?: boolean; autoFocus?: boolean;
}) {
  const [text, setText] = useState(value === null ? "" : paiseToRupees(value));
  const [local, setLocal] = useState<string | null>(null);
  return (
    <Field label={label} error={error ?? local} hint={hint} required={required}>
      {(id, d, invalid) => (
        <input id={id} className="tos-input tos-num" inputMode="decimal" aria-describedby={d} aria-invalid={invalid || undefined} disabled={disabled} required={required}
          autoFocus={autoFocus} value={text} placeholder="0.00"
          onChange={(e) => {
            const t = e.target.value;
            setText(t);
            if (!t.trim()) { setLocal(null); onChange(null); return; }
            try { onChange(rupeesToPaise(t)); setLocal(null); } catch { setLocal("Enter an amount like 501 or 501.50"); onChange(null); }
          }} />
      )}
    </Field>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search…", label = "Search" }: { value: string; onChange: (v: string) => void; placeholder?: string; label?: string }) {
  const id = useId();
  return (
    <div className="tos-field" style={{ minWidth: 220 }}>
      <label className="tos-sr-only" htmlFor={id}>{label}</label>
      <input id={id} type="search" className="tos-input" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function DateRange({ from, to, onChange }: { from: string; to: string; onChange: (r: { from: string; to: string }) => void }) {
  return (
    <div className="tos-row" style={{ alignItems: "flex-end" }}>
      <TextField label="From" type="date" value={from} max={to || undefined} onChange={(e) => onChange({ from: e.target.value, to })} />
      <TextField label="To" type="date" value={to} min={from || undefined} onChange={(e) => onChange({ from, to: e.target.value })} />
    </div>
  );
}
